import React, { useState } from "react";
import axios from "axios";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { ArrowUpRight, Instagram, Mail, Phone, MapPin, Music2 } from "lucide-react";
import { STUDIO } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Preenche nome, email e mensagem.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/contacts`, form);
      toast.success("Mensagem enviada. Obrigado! ✦");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Não foi possível enviar. Tenta novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-ink text-cream py-24 md:py-32 px-5 md:px-10 lg:px-16 overflow-hidden">
      <div aria-hidden className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-hot/15 blur-[120px]" />
      <div className="relative z-10 grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-5">
          <div className="inline-flex items-center gap-2 bg-hot/10 border border-hot/30 rounded-full px-4 py-2 mb-6">
            <span className="font-body text-xs tracking-widest uppercase text-hot">Contacto</span>
          </div>
          <h2 className="font-display font-extrabold text-5xl md:text-7xl leading-[0.92] mb-10">
            diz <span className="text-lime italic">olá</span>. <br/>
            <span className="font-hand text-6xl text-hot">não mordemos.</span>
          </h2>
          <div className="space-y-4 font-body text-sm">
            <ContactRow icon={<MapPin size={18} />} label={STUDIO.address} />
            <ContactRow icon={<Phone size={18} />} label={STUDIO.phone} href={`tel:${STUDIO.phone.replace(/\s/g, "")}`} testid="contact-phone-link" />
            <ContactRow icon={<Mail size={18} />} label={STUDIO.email} href={`mailto:${STUDIO.email}`} testid="contact-email-link" />
            <ContactRow icon={<Instagram size={18} />} label={STUDIO.instagram} href={STUDIO.instagramUrl} testid="contact-instagram-link" external />
            <ContactRow icon={<Music2 size={18} />} label={STUDIO.tiktok} href={STUDIO.tiktokUrl} testid="contact-tiktok-link" external />
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="col-span-12 md:col-span-7 bg-white/5 border-2 border-white/10 backdrop-blur rounded-3xl p-6 md:p-10 space-y-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LabelWrap label="Nome">
              <Input data-testid="contact-name-input" value={form.name} onChange={(e) => update("name", e.target.value)} className="rounded-xl h-12 bg-transparent border-cream/20 text-cream placeholder:text-cream/30 font-body" placeholder="O teu nome" />
            </LabelWrap>
            <LabelWrap label="Email">
              <Input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-xl h-12 bg-transparent border-cream/20 text-cream placeholder:text-cream/30 font-body" placeholder="email@dominio.pt" />
            </LabelWrap>
          </div>
          <LabelWrap label="Assunto">
            <Input data-testid="contact-subject-input" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="rounded-xl h-12 bg-transparent border-cream/20 text-cream placeholder:text-cream/30 font-body" placeholder="Ex: orçamento videoclip" />
          </LabelWrap>
          <LabelWrap label="Mensagem">
            <Textarea data-testid="contact-message-input" value={form.message} onChange={(e) => update("message", e.target.value)} className="rounded-xl bg-transparent border-cream/20 text-cream placeholder:text-cream/30 font-body min-h-[140px]" placeholder="Fala-nos do teu projeto..." />
          </LabelWrap>
          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={loading}
            className="group w-full inline-flex items-center justify-center gap-3 bg-lime text-ink font-display font-bold text-base px-6 py-4 rounded-full hover:bg-hot hover:text-cream transition-colors disabled:opacity-50"
          >
            {loading ? "A enviar..." : "Enviar mensagem ✦"}
            <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </button>
        </form>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, href, testid, external }) {
  const cls = "flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl px-5 py-4 hover:bg-lime hover:text-ink hover:border-lime transition-colors";
  const inner = <><span className="text-lime group-hover:text-ink">{icon}</span><span className="font-body">{label}</span></>;
  if (href) {
    return (
      <a href={href} data-testid={testid} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} className={`group ${cls}`}>
        {inner}
      </a>
    );
  }
  return <div className={`group ${cls}`}>{inner}</div>;
}

function LabelWrap({ label, children }) {
  return (
    <label className="block">
      <span className="font-body text-xs font-bold uppercase tracking-wider text-cream/50 block mb-2">{label}</span>
      {children}
    </label>
  );
}
